import { Routes, Route } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { SelectedRestaurantProvider } from './context/SelectedRestaurantContext';
import { RestaurantCacheProvider } from './context/RestaurantCacheContext';
import { AppProvider } from './context/AppContext';
import { TutorialRefsProvider } from './context/TutorialRefsContext';
import { WaiterShiftProvider } from './context/WaiterShiftContext';
import { NotificationProvider } from './context/NotificationContext';
import { NotificationStateProvider } from './context/NotificationStateContext';
import ErrorBoundary from './components/ErrorBoundary';

import HomePage from './pages/HomePage';
import MenuPage from './pages/MenuPage';
import ProfilePage from './pages/ProfilePage';
import LoginPage from './pages/LoginPage'; 
//import PricingPage from './pages/PricingPage'; 
import PricingPageStripe from './pages/PricingPageStripe';
import NotFoundPage from './pages/NotFoundPage';
import TermsPage from './pages/TermsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import AdminLayout from './layouts/AdminLayout';
import Dashboard from './pages/admin/Dashboard';
import RestaurantsPage from './pages/admin/RestaurantsPage';
import OrdersPage from './pages/admin/OrdersPage';
import AnalyticsPage from './pages/admin/AnalyticsPage';
import TablesPage from './pages/admin/TablesPage';
import TableLayoutPage from './pages/admin/TableLayoutPage';
import WaitersPage from './pages/admin/WaitersPage';
import ResponsiblesPage from './pages/admin/Responsibles';
import KitchenPage from './pages/admin/Kitchen';
import MenuPageAdmin from './pages/admin/MenuPage';
import ReservationsPage from './pages/admin/ReservationsPage';
import PromotionsPage from './pages/admin/PromotionsPage';
import Sales from './pages/admin/Sales';
import WaiterHome from './pages/waiter/Home';
import WaiterTablesPage from './pages/waiter/TablesPage';
import ProtectedRoute from './components/ProtectedRoute';
import './styles/globals.css';

// Importar componentes de cliente
import ClientLayout from './pages/client/ClientLayout';
import ClientRestaurantsPage from './pages/client/RestaurantsPage';
import ClientOrdersHistoryPage from './pages/client/OrdersHistoryPage';
import ClientTablesPage from './pages/client/TablesPage';

// Importar hook de auto-refresh
import { PWASplashScreen } from './components/PWASplashScreen';
import { AnalyticsRouteTracker } from './analytics/analytics';

// Importar componente de debug (solo desarrollo)
//import TokenStatusDebug from './components/TokenStatusDebug';
import { useState } from 'react';

function App() {
  // Timestamp para forzar recarga del navegador
  console.log('🔄 App.tsx recargado:', Date.now());
  
  // Estado para controlar el splash screen - siempre visible al inicio
  const [showSplash, setShowSplash] = useState(true);
  
  const handleSplashComplete = () => {
    console.log('✅ Splash completado, ocultando...');
    setShowSplash(false);
  };
  
  // Debug: mostrar estado del splash
  console.log('🎭 App renderizando, showSplash:', showSplash);
  
  return (
    <>
      {/* Splash Screen PWA - siempre visible al inicio */}
      {showSplash && (
        <div 
          key="splash-screen" 
          className="splash-container"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            backgroundColor: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <PWASplashScreen onLoadComplete={handleSplashComplete} />
        </div>
      )}
      
      <ErrorBoundary>
        <AuthProvider>
          <SelectedRestaurantProvider>
            <RestaurantCacheProvider>
              <AppProvider>
                <TutorialRefsProvider>
                  <WaiterShiftProvider>
                    <NotificationProvider>
                      <NotificationStateProvider>
                        <AnalyticsRouteTracker />
                        <Routes>
                          {/* Ruta raíz */}
                          <Route path="/" element={<HomePage />} />
                          {/* Redirección para compatibilidad: /home -> / */}
                          <Route path="/menu" element={<MenuPage />} />
                          <Route path="/profile" element={<ProfilePage />} />
                          
                          {/* Rutas públicas */}
                          <Route path="login" element={<LoginPage />} />
                          {/*<Route path="pricing" element={<PricingPage />} />*/}
                          <Route path="pricing" element={<PricingPageStripe />} />
                          <Route path="terminos" element={<TermsPage />} />
                          <Route path="privacidad" element={<PrivacyPolicyPage />} />
                          
                          {/* Rutas protegidas de admin */}
                          <Route path="admin" element={
                            <ProtectedRoute requireAuth={true} requireAdmin={true}>
                              <AdminLayout />
                            </ProtectedRoute>
                          }>
                            <Route index element={<Dashboard />} />
                            <Route path="restaurants" element={<RestaurantsPage />} />
                            <Route path="orders" element={<OrdersPage />} />
                            <Route path="analytics" element={<AnalyticsPage />} />
                            <Route path="sales" element={<Sales />} />
                            <Route path="tables" element={<TablesPage />} />
                            <Route path="tables/layout" element={<TableLayoutPage />} />
                            <Route path="waiters" element={<WaitersPage />} />
                            <Route path="responsibles" element={<ResponsiblesPage />} />
                            <Route path="kitchen" element={<KitchenPage />} />
                            <Route path="reservations" element={<ReservationsPage />} />
                            <Route path="promotions" element={<PromotionsPage />} />
                            <Route path="menu" element={<MenuPageAdmin />} />
                          </Route>
                          
                          {/* Rutas de cliente */}
                          <Route path="client" element={
                            <ProtectedRoute requireAuth={true}>
                              <ClientLayout />
                            </ProtectedRoute>
                          }>
                            <Route index element={<ClientRestaurantsPage />} />
                            <Route path="restaurant/:restaurantId/tables" element={<ClientTablesPage />} />
                            <Route path="restaurant/:restaurantId/orders" element={<ClientOrdersHistoryPage />} />
                          </Route>
                          
                          {/* Rutas protegidas de mesero */}
                          <Route path="waiter" element={
                            <ProtectedRoute requireAuth={true}>
                              <WaiterHome />
                            </ProtectedRoute>
                          } />
                          <Route path="waiter/tables" element={
                            <ProtectedRoute requireAuth={true}>
                              <WaiterTablesPage />
                            </ProtectedRoute>
                          } />
                          {/* 404 */}
                          <Route path="*" element={<NotFoundPage />} />
                        </Routes>
                      </NotificationStateProvider>
                    </NotificationProvider>
                  </WaiterShiftProvider>
                </TutorialRefsProvider>
              </AppProvider>
            </RestaurantCacheProvider>
          </SelectedRestaurantProvider>
        </AuthProvider>
      </ErrorBoundary>
    </>
  );
}

export default App; 